import React, {useState} from "react";
import {callApiUpdateOptiselEventNote} from "./useOptiselEventApi";
import {useHasCurrentUserPrivilege} from "../../layout/Protected";

const MAX_NOTE_LENGTH = 100;

type Mode = "viewing" | "editing" | "saving";

export function OptiselEventNoteEditor(props: {
    eventId: string,
    initialNote: string,
    onNoteUpdated?: (newNote: string) => void
}) {
    const canEdit = useHasCurrentUserPrivilege("PERM_HISTORY_AND_ISSUES_MANAGEMENT") === true;
    const [displayedNote, setDisplayedNote] = useState(props.initialNote);
    const [mode, setMode] = useState<Mode>("viewing");
    const [draft, setDraft] = useState("");

    const startEditing = () => {
        setDraft(displayedNote);
        setMode("editing");
    };

    const cancel = () => {
        setMode("viewing");
        setDraft("");
    };

    const save = () => {
        setMode("saving");
        callApiUpdateOptiselEventNote(props.eventId, draft)
            .then(response => {
                setDisplayedNote(response.note);
                props.onNoteUpdated?.(response.note);
                setMode("viewing");
            })
            .catch(() => {
                window.alert("Nie udało się zapisać notatki. Spróbuj ponownie.");
                setMode("editing");
            });
    };

    if (mode === "viewing") {
        return renderViewing(displayedNote, canEdit ? startEditing : null);
    }
    if (mode === "saving") {
        return renderSaving(draft);
    }
    return renderEditing(draft, setDraft, save, cancel);
}

function renderViewing(note: string, onEdit: (() => void) | null) {
    const hasNote = note !== "";
    const noteDisplay = hasNote
        ? <span className="text-gray-700" style={{whiteSpace: "pre-wrap"}}>{note}</span>
        : <span className="text-gray-400 fst-italic">brak notatki</span>;
    return <div className="d-flex align-items-center gap-2">
        {noteDisplay}
        {onEdit && <button className="btn btn-sm btn-light-primary" title="Edytuj notatkę" onClick={onEdit}>
            <i className="fas fa-pencil-alt"/>
        </button>}
    </div>;
}

function renderEditing(draft: string, setDraft: (s: string) => void, onSave: () => void, onCancel: () => void) {
    const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") onSave();
        if (e.key === "Escape") onCancel();
    };
    return <div className="d-flex align-items-center gap-2">
        <input
            type="text"
            className="form-control form-control-sm"
            value={draft}
            maxLength={MAX_NOTE_LENGTH}
            onChange={e => setDraft(e.target.value)}
            autoFocus
            onKeyDown={handleKey}
        />
        <button className="btn btn-sm btn-light-primary" title="Zapisz" onClick={onSave}>
            <i className="fas fa-check"/>
        </button>
        <button className="btn btn-sm btn-light" title="Anuluj" onClick={onCancel}>
            <i className="fas fa-times"/>
        </button>
    </div>;
}

function renderSaving(draft: string) {
    return <div className="d-flex align-items-center gap-2">
        <i className="fas fa-spinner fa-spin"/>
        <span className="text-gray-500">{draft}</span>
    </div>;
}
